<template lang="html">
  <!-- We disabled this since the table only accepts simple values. -->
  <!-- eslint-disable vue/require-v-for-key -->
  <section
    class="formatted-table"
    :class="{
      'is-mobile': isMobile,
      'is-gt-mobile': isGtMobile,
      'is-centered': centered,
      'is-wrapped': wrapped,
      'has-clickable-items': clickableItems,
      'has-single-line-rows': singleLineRows,
    }"
    :data-cy="dataCy"
    @click="$emit('click', $event)"
  >
    <BaseSpinner v-if="isLoading" />
    <div v-else-if="noItems" class="no-items">
      {{ noItemsMessage }}
    </div>

    <template v-else-if="isMobile">
      <template v-if="rowSlotPrefixes.length > 0">
        <table
          v-for="rowPrefix in rowSlotPrefixes"
          class="formatted-table-inner item-section is-mobile"
        >
          <tbody>
            <tr v-for="(name, cellIndex) in cellSlots(rowPrefix)">
              <td v-if="headerSlots.length > 0" class="cell-header-mobile">
                <slot :name="headerSlots[cellIndex]"></slot>
              </td>
              <td><slot :name="name"></slot></td>
            </tr>
          </tbody>
        </table>
      </template>

      <table
        v-for="(item, index) in items"
        v-else
        class="formatted-table-inner item-section is-mobile"
      >
        <tbody>
          <tr v-for="(value, header) in item">
            <td class="cell-header-mobile">
              <!-- TODO: Use component or partial to make this DRY with mobile -->
              <div class="header-contents">
                <slot
                  name="cell"
                  :is-header="true"
                  :is-mobile="true"
                  :content="header"
                  :metadata="itemsMetadata[index]"
                  :tooltip-text="tooltips[header]"
                >
                  <QuestionTooltip
                    :trigger-text="header"
                    :tooltip-text="tooltips[header]"
                  />
                </slot>
              </div>
            </td>

            <!-- NOTE: `hide-in-percy` prevents false positives with dates. -->
            <td
              class="cell-contents hide-in-percy"
              :class="{ 'is-empty': value == null || value === '' }"
              :data-column-name="header.toLowerCase()"
            >
              <slot
                name="cell"
                :is-header="false"
                :is-mobile="true"
                :content="value"
                :metadata="itemsMetadata[index]"
                :header-name="header.toLowerCase()"
                >{{ value }}</slot
              >
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <table v-else class="formatted-table-inner">
      <thead>
        <tr v-if="headerSlots.length > 0">
          <th v-for="name in headerSlots"><slot :name="name"></slot></th>
        </tr>

        <tr v-else>
          <th
            v-for="(header, index) in headers"
            :data-column-name="header.toLowerCase()"
          >
            <!-- TODO: Use component or partial to make this DRY with mobile -->
            <div class="header-contents">
              <slot
                name="cell"
                :is-header="true"
                :is-mobile="false"
                :content="header"
                :metadata="itemsMetadata[index]"
                :tooltip-text="tooltips[header]"
              >
                <QuestionTooltip
                  :trigger-text="header"
                  :tooltip-text="tooltips[header]"
                />
              </slot>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <template v-if="rowSlotPrefixes.length > 0">
          <tr v-for="rowPrefix in rowSlotPrefixes" class="item-section">
            <template v-for="name in cellSlots(rowPrefix)">
              <td><slot :name="name"></slot></td>
            </template>
          </tr>
        </template>

        <!--
        TODO: This is not possible with `transition-group` because it injects
        an element and creates invalid HTML. Consider adding that anyways once
        `display: contents` is standard.
        See https://caniuse.com/#feat=css-display-contents
        -->
        <tr
          v-for="(row, index) in rows"
          v-else
          :key="index"
          class="item-section"
        >
          <!-- NOTE: `hide-in-percy` prevents false positives with dates. -->
          <td
            v-for="(value, rowIndex) in row"
            class="hide-in-percy"
            :class="{ 'is-empty': value == null || value === '' }"
            :data-column-name="headers[rowIndex].toLowerCase()"
          >
            <slot
              name="cell"
              :is-header="false"
              :is-mobile="false"
              :content="value"
              :metadata="itemsMetadata[index]"
              :header-name="headers[rowIndex].toLowerCase()"
            >
              {{ value }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
  <!-- eslint-enable vue/require-v-for-key -->
</template>

<script>
import { values } from 'lodash';
import mobileDetection from '@/mixins/mobile-detection';
import QuestionTooltip from '@/components/QuestionTooltip';

// Creates a table that formats nicely on both mobile and desktop. To save
// space, the mobile form uses a vertical layout where each record corresponds
// to one table. The desktop layout uses a traditional table where each row
// corresponds to one record.
//
// Props that modify visual appearance:
//
// TODO: Document this.
// centered
//
// TODO: Document this.
// wrapped
//
// TODO: Document this.
// clickableItems
//
// singleLineRows: Forces rows to be a single line with ellipses. Only
// applies in the desktop layout.
export default {
  name: 'FormattedTable',

  components: {
    QuestionTooltip,
  },

  mixins: [mobileDetection],

  props: {
    // Format for items:
    // {
    //   'Players Card': 'Insurance?',
    //   'Dealers Card': '11',
    //   'Correct Move': 'Y',
    //   'Index': '>= 3'
    // },
    // {
    //   'Players Card': '16',
    //   'Dealers Card': '10',
    //   'Correct Move': 'S',
    //   'Index': '>= 0'
    // }
    items: {
      type: Array,
      default: () => [],
    },

    itemsMetadata: {
      type: Array,
      default: () => [],
    },

    // Format for tooltips:
    // {
    //   'Players Card': 'Tooltip text A',
    //   'Dealers Card': 'Tooltip text B',
    //   'Correct Move': 'Tooltip text C',
    //   'Index': 'Tooltip text D'
    // }
    tooltips: {
      type: Object,
      default: () => ({}),
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    noItemsMessage: {
      type: String,
      default: 'No items',
    },

    centered: {
      type: Boolean,
      default: false,
    },

    wrapped: {
      type: Boolean,
      default: true,
    },

    clickableItems: {
      type: Boolean,
      default: false,
    },

    singleLineRows: {
      type: Boolean,
      default: false,
    },

    dataCy: {
      type: String,
      default: null,
    },
  },

  computed: {
    rows() {
      return this.items.map((i) => values(i));
    },

    headers() {
      return this.items.length > 0 ? Object.keys(this.items[0]) : [];
    },

    noItems() {
      if (this.rowSlotPrefixes.length > 0) {
        return false;
      }

      return !this.items || this.items.length === 0;
    },

    rowSlotPrefixes() {
      return Array.from(
        new Set(
          Object.keys(this.$slots)
            .filter((name) => name.startsWith('row-'))
            .map((name) => name.split('-cell')[0])
        )
      );
    },

    headerSlots() {
      return this.slots('header-');
    },
  },

  methods: {
    slots(prefix) {
      return Object.keys(this.$slots).filter((k) => k.startsWith(prefix));
    },

    // TODO: Cache.
    cellSlots(rowPrefix) {
      return this.slots(rowPrefix);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/mixins/ellipsis';

$table-spacing: 0.5em;
$table-vertical-margin: 1.5em;

.formatted-table {
  position: relative;
  min-height: 4rem; // Adds height while loading.
  margin: 2em 0;
}

th {
  text-align: left;
}

th,
.cell-header-mobile {
  font-weight: map-get($font-weights, 'bold-2x');
}

td,
th {
  padding-top: $table-spacing;
  padding-bottom: $table-spacing;
  vertical-align: top;
}

.header-contents {
  display: flex;
  align-items: center;
}

.has-clickable-items {
  // Using v-deep in case the caller of this component uses slots.
  ::v-deep {
    .item-section {
      cursor: pointer;

      &:hover {
        background-color: map-get($colors, 'blue-light-16x');
      }
    }

    a {
      &,
      &:hover {
        text-decoration: none;
        color: inherit;
      }
    }
  }
}

.is-empty {
  &::before {
    content: '—';
    display: inline-block;
    color: map-get($colors, 'grey');
  }
}

.is-mobile {
  // Effectively adds vertical padding around the table by applying it to cells
  // in the first and last rows. This assumes formatted table does not use
  // tfoot.
  tbody:first-child {
    tr:first-child {
      td,
      th {
        padding-top: $table-vertical-margin;
      }
    }
  }

  tbody:last-child {
    tr:last-child {
      td,
      th {
        padding-bottom: $table-vertical-margin;
      }
    }
  }

  td {
    &:last-child {
      // Ensure values don't wrap (particularly dollar values).
      // @include ellipsis;
      text-align: right;
    }
  }

  // Using v-deep in case the caller of this component uses slots.
  ::v-deep {
    // Targets the data column. We avoid using `:last-child` since the mobile
    // table is allowed to have a single column here. We only apply if both
    // columns are present.
    .cell-header-mobile ~ td {
      .item-link {
        justify-content: flex-end;
      }
    }
  }
}

.formatted-table-inner {
  width: 100%;
}

.is-wrapped {
  // We use the vertical padding amount for general spacing since we are
  // wrapping styles already defined in `_formatted-table.scss`.
  $spacing: $table-vertical-margin;

  td,
  th {
    padding-left: $table-spacing;
    padding-right: $table-spacing;
  }

  .formatted-table-inner {
    margin-bottom: $spacing;
    background-color: map-get($colors, 'faded-background-1x');
    box-shadow: 0px 1px 2px map-get($colors, 'faded-background-5x');
    border-radius: map-get($app, 'border-radius');

    &:last-child {
      margin-bottom: 0;
    }
  }

  .no-items {
    text-align: center;
    padding: $spacing;
  }

  &.is-mobile {
    .no-items,
    .formatted-table-inner {
      // Using v-deep in case the caller of this component uses slots.
      ::v-deep {
        th,
        td {
          padding-left: $spacing;
          padding-right: $spacing;
        }

        // Sometimes we want an entire cell to be a link. So we reverse the `<td>`
        // padding and apply our own in the child `<a>` element.
        .item-link {
          display: flex;
          padding: $spacing $spacing;
          margin: -$spacing (-$spacing);
        }
      }
    }

    // Hide tooltips on mobile.
    // TODO: Make these clickable on mobile then make them visible.
    [data-icon] {
      display: none;
    }
  }

  &.is-gt-mobile {
    .formatted-table-inner {
      border-collapse: collapse;
    }

    $spacing-v: $spacing / 2;
    $spacing-h: $spacing;

    // Using v-deep in case the caller of this component uses slots.
    ::v-deep {
      td,
      th {
        padding: $spacing-v $spacing-h;
        border-bottom: 1px solid map-get($colors, 'faded-background-2x');
      }

      // Sometimes we want an entire cell to be a link. So we reverse the `<td>`
      // padding and apply our own in the child `<a>` element.
      .item-link {
        display: flex;
        padding: $spacing-v $spacing-h;
        margin: -$spacing-v (-$spacing-h);
      }

      tr:last-child td {
        border-bottom: none;
      }
    }
  }
}

.is-centered {
  &.is-gt-mobile {
    text-align: center;

    .formatted-table-inner {
      .header-contents {
        justify-content: center;
        text-align: center;
      }
    }
  }
}

.has-single-line-rows {
  &.is-gt-mobile {
    table {
      table-layout: fixed;
    }

    // Using v-deep in case the caller of this component uses slots.
    ::v-deep {
      th,
      td,
      .item-link {
        @include ellipsis;
      }

      .item-link {
        // Links inside the cells won't show ellipsis unless we do this.
        display: block;
      }
    }
  }
}
</style>
